import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus, Edit } from "lucide-react";
import { toast } from "sonner";
import {
  useListPickupSlots,
  useCreatePickupSlot,
  useUpdatePickupSlot,
  useDeletePickupSlot,
  useUpsertPickupSlotCapacityOverride,
  useDeletePickupSlotCapacityOverride,
  type PickupSlot,
  type PickupSlotCapacityOverride,
  type PickupServiceDay,
  type CreatePickupSlotInput,
} from "@/api/pickup-slots";
import { useGetDispatchCenters } from "@/api/dispatch-centers";

interface Props {
  serviceId: string;
}

const PICKUP_SERVICE_DAY_OPTIONS: ReadonlyArray<{
  value: PickupServiceDay;
  shortLabel: string;
  label: string;
}> = [
  { value: "MON", shortLabel: "Mon", label: "Monday" },
  { value: "TUE", shortLabel: "Tue", label: "Tuesday" },
  { value: "WED", shortLabel: "Wed", label: "Wednesday" },
  { value: "THU", shortLabel: "Thu", label: "Thursday" },
  { value: "FRI", shortLabel: "Fri", label: "Friday" },
  { value: "SAT", shortLabel: "Sat", label: "Saturday" },
  { value: "SUN", shortLabel: "Sun", label: "Sunday" },
];

const ALL_PICKUP_SERVICE_DAYS = PICKUP_SERVICE_DAY_OPTIONS.map(
  (option) => option.value
);

function pickupServiceDaysFromValues(values: string[]): PickupServiceDay[] {
  const selected = new Set(values);
  return ALL_PICKUP_SERVICE_DAYS.filter((day) => selected.has(day));
}

function formatPickupServiceDays(days: PickupServiceDay[]): string {
  const selected = new Set(days);
  const ordered = ALL_PICKUP_SERVICE_DAYS.filter((day) => selected.has(day));
  if (ordered.length === ALL_PICKUP_SERVICE_DAYS.length) return "Every day";
  if (
    ordered.length === 6 &&
    ordered.every((day, index) => day === ALL_PICKUP_SERVICE_DAYS[index])
  ) {
    return "Mon–Sat";
  }
  return ordered
    .map(
      (day) =>
        PICKUP_SERVICE_DAY_OPTIONS.find((option) => option.value === day)
          ?.shortLabel
    )
    .filter(Boolean)
    .join(", ");
}

/**
 * Pickup slot management tab on the pickup-service detail screen.
 *
 * Slots are owned by (this service, a dispatch center). Each row in the
 * table is one slot at one dispatch — same window at two dispatches is
 * two rows. Edit dialog carries everything operational about the slot
 * plus a nested per-date capacity overrides editor.
 */
export function PickupSlotsTab({ serviceId }: Props) {
  const { data: slots = [], isLoading } = useListPickupSlots(serviceId);
  const { data: dispatches = [] } = useGetDispatchCenters();
  const [creating, setCreating] = useState(false);
  const [editingSlot, setEditingSlot] = useState<PickupSlot | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold">Pickup slots</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Time windows and service days control when customers can book
            pickup. Delivery run schedules separately control onward delivery
            estimates.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4 mr-1" /> New slot
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Dispatch</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Service days</TableHead>
              <TableHead>Lead (min)</TableHead>
              <TableHead>Default cap</TableHead>
              <TableHead>Overrides</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : slots.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-8 text-muted-foreground"
                >
                  No slots yet. Add one to start accepting bookings.
                </TableCell>
              </TableRow>
            ) : (
              slots.map((s) => (
                <SlotRow key={s.id} slot={s} onEdit={() => setEditingSlot(s)} />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {creating && (
        <SlotDialog
          mode="create"
          serviceId={serviceId}
          dispatches={dispatches}
          onClose={() => setCreating(false)}
        />
      )}
      {editingSlot && (
        <SlotDialog
          mode="edit"
          slot={editingSlot}
          serviceId={serviceId}
          dispatches={dispatches}
          onClose={() => setEditingSlot(null)}
        />
      )}
    </div>
  );
}

function SlotRow({ slot, onEdit }: { slot: PickupSlot; onEdit: () => void }) {
  const deleteSlot = useDeletePickupSlot({
    onSuccess: () => toast.success("Slot deleted"),
    onError: (e) => toast.error("Delete failed", { description: e.message }),
  });
  return (
    <TableRow>
      <TableCell className="font-medium">{slot.name}</TableCell>
      <TableCell className="text-sm">
        {slot.dispatchCenter?.name ?? slot.dispatchCenterId}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {slot.startTime}–{slot.endTime}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatPickupServiceDays(slot.serviceDays)}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {slot.bookingLeadMinutes}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {slot.defaultCapacity == null ? "Unlimited" : slot.defaultCapacity}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {slot.capacityOverrides && slot.capacityOverrides.length > 0
          ? `${slot.capacityOverrides.length} date${slot.capacityOverrides.length === 1 ? "" : "s"}`
          : "—"}
      </TableCell>
      <TableCell>
        <span className="text-xs">{slot.isActive ? "Active" : "Inactive"}</span>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onEdit}
            aria-label={`Edit ${slot.name}`}
          >
            <Edit className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              if (confirm(`Delete slot "${slot.name}"?`)) {
                deleteSlot.mutate(slot.id);
              }
            }}
            disabled={deleteSlot.isPending}
            aria-label={`Delete ${slot.name}`}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function SlotDialog({
  mode,
  slot,
  serviceId,
  dispatches,
  onClose,
}: {
  mode: "create" | "edit";
  slot?: PickupSlot;
  serviceId: string;
  dispatches: { id: string; code: string; name: string }[];
  onClose: () => void;
}) {
  const [form, setForm] = useState<CreatePickupSlotInput>({
    dispatchCenterId: slot?.dispatchCenterId ?? dispatches[0]?.id ?? "",
    name: slot?.name ?? "",
    startTime: slot?.startTime ?? "09:00",
    endTime: slot?.endTime ?? "12:00",
    serviceDays: slot ? [...slot.serviceDays] : [...ALL_PICKUP_SERVICE_DAYS],
    bookingLeadMinutes: slot?.bookingLeadMinutes ?? 60,
    defaultCapacity: slot?.defaultCapacity ?? null,
    isActive: slot?.isActive ?? true,
    sortOrder: slot?.sortOrder ?? 0,
  });
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useCreatePickupSlot(serviceId, {
    onSuccess: () => {
      toast.success("Slot created");
      onClose();
    },
    onError: (e) => toast.error("Create failed", { description: e.message }),
  });
  const updateMutation = useUpdatePickupSlot({
    onSuccess: () => {
      toast.success("Slot updated");
      onClose();
    },
    onError: (e) => toast.error("Update failed", { description: e.message }),
  });

  const handleSave = () => {
    setFormError(null);
    if (form.serviceDays.length === 0) {
      setFormError("Choose at least one pickup service day.");
      return;
    }
    if (mode === "create") {
      createMutation.mutate(form);
    } else if (slot) {
      updateMutation.mutate({ slotId: slot.id, data: form });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "New pickup slot"
              : `Edit ${slot?.name ?? "slot"}`}
          </DialogTitle>
          <DialogDescription>
            Configure the dispatch-local pickup window, customer booking days,
            lead time, and capacity.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-xs">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Morning"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Dispatch</Label>
            <Select
              value={form.dispatchCenterId}
              onValueChange={(v) => setForm({ ...form, dispatchCenterId: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick a dispatch" />
              </SelectTrigger>
              <SelectContent>
                {dispatches.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name} ({d.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Start</Label>
              <Input
                type="time"
                value={form.startTime}
                onChange={(e) =>
                  setForm({ ...form, startTime: e.target.value })
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End</Label>
              <Input
                type="time"
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              />
            </div>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-xs font-medium">Pickup service days</legend>
            <p className="text-[11px] text-muted-foreground">
              Customers can book this pickup window only on selected days.
            </p>
            <ToggleGroup
              type="multiple"
              variant="outline"
              size="sm"
              value={form.serviceDays}
              onValueChange={(values) =>
                setForm({
                  ...form,
                  serviceDays: pickupServiceDaysFromValues(values),
                })
              }
              aria-label="Pickup service days"
              className="grid w-full grid-cols-4 sm:grid-cols-7"
            >
              {PICKUP_SERVICE_DAY_OPTIONS.map((option) => (
                <ToggleGroupItem
                  key={option.value}
                  value={option.value}
                  aria-label={option.label}
                  className="px-2"
                >
                  {option.shortLabel}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </fieldset>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Lead time (min)</Label>
              <Input
                type="number"
                min={0}
                value={form.bookingLeadMinutes ?? 60}
                onChange={(e) =>
                  setForm({
                    ...form,
                    bookingLeadMinutes: parseInt(e.target.value, 10) || 0,
                  })
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Default capacity</Label>
              <Input
                type="number"
                min={0}
                placeholder="Unlimited"
                value={form.defaultCapacity ?? ""}
                onChange={(e) => {
                  const raw = e.target.value.trim();
                  setForm({
                    ...form,
                    defaultCapacity: raw === "" ? null : parseInt(raw, 10),
                  });
                }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <div className="space-y-1">
              <Label className="text-xs">Sort order</Label>
              <Input
                type="number"
                value={form.sortOrder ?? 0}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sortOrder: parseInt(e.target.value, 10) || 0,
                  })
                }
                className="w-24"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.isActive ?? true}
                onCheckedChange={(v) => setForm({ ...form, isActive: v })}
              />
              <Label className="text-xs">Active</Label>
            </div>
          </div>

          {/* Per-date overrides only on edit (need an existing slot ID). */}
          {mode === "edit" && slot && (
            <CapacityOverridesPanel
              slotId={slot.id}
              overrides={slot.capacityOverrides ?? []}
            />
          )}

          {formError && (
            <p role="alert" className="text-xs text-destructive">
              {formError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CapacityOverridesPanel({
  slotId,
  overrides,
}: {
  slotId: string;
  overrides: PickupSlotCapacityOverride[];
}) {
  const [newDate, setNewDate] = useState("");
  const [newCap, setNewCap] = useState("");

  const upsert = useUpsertPickupSlotCapacityOverride({
    onSuccess: () => {
      setNewDate("");
      setNewCap("");
      toast.success("Override saved");
    },
    onError: (e) => toast.error("Save failed", { description: e.message }),
  });
  const remove = useDeletePickupSlotCapacityOverride({
    onSuccess: () => toast.success("Override removed"),
    onError: (e) => toast.error("Delete failed", { description: e.message }),
  });

  return (
    <div className="space-y-2 pt-3 border-t">
      <Label className="text-xs font-semibold">
        Per-date capacity overrides
      </Label>
      <p className="text-[10px] text-muted-foreground">
        Override the default cap for specific dates (holidays, busy days,
        closures). Max = 0 means closed.
      </p>

      {overrides.length > 0 && (
        <div className="space-y-1">
          {overrides.map((o) => (
            <div
              key={o.id}
              className="flex items-center justify-between text-xs bg-muted rounded px-2 py-1"
            >
              <span className="font-mono">{o.date.slice(0, 10)}</span>
              <span className="text-muted-foreground">
                {o.maxCapacity === 0 ? "Closed" : `Max ${o.maxCapacity}`}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => remove.mutate(o.id)}
                disabled={remove.isPending}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1">
          <Label className="text-[10px]">Date</Label>
          <Input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            className="h-8"
          />
        </div>
        <div className="w-24 space-y-1">
          <Label className="text-[10px]">Max</Label>
          <Input
            type="number"
            min={0}
            value={newCap}
            onChange={(e) => setNewCap(e.target.value)}
            className="h-8"
          />
        </div>
        <Button
          size="sm"
          onClick={() => {
            const cap = parseInt(newCap, 10);
            if (!newDate || Number.isNaN(cap)) return;
            upsert.mutate({ slotId, date: newDate, maxCapacity: cap });
          }}
          disabled={upsert.isPending || !newDate || newCap === ""}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
